import { Component, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { IconFieldModule } from 'primeng/iconfield';
import { InputIconModule } from 'primeng/inputicon';
import { AuthService } from '@services/auth.service';
import { PageHeaderComponent } from '../../../components/shared/page-header/page-header.component';
import { EmptyStateComponent } from '../../../components/shared/empty-state/empty-state.component';

/** Every venue the user can open, with shortcuts into that venue's own admin (calendar first). */
@Component({
    selector: 'venues',
    imports: [FormsModule, TranslatePipe, ButtonModule, InputTextModule, IconFieldModule, InputIconModule, PageHeaderComponent, EmptyStateComponent],
    template: `
        <fb-page-header [title]="'VENUES' | translate" [subtitle]="'VENUES_SUBTITLE' | translate">
            @if (auth.accessibleVenues().length > 6) {
                <p-iconfield actions>
                    <p-inputicon class="ph ph-magnifying-glass" />
                    <input pInputText type="search" [placeholder]="'SEARCH_VENUES' | translate" [ngModel]="search()" (ngModelChange)="search.set($event)" />
                </p-iconfield>
            }
        </fb-page-header>

        <div class="grid grid-cols-1 md:grid-cols-2 2xl:grid-cols-3 gap-4">
            @for (venue of venues(); track venue._id) {
                <article class="flex flex-col gap-4 p-5 rounded-md bg-surface border border-border hover:border-border-strong transition-colors">
                    <div class="flex items-start gap-3">
                        <span class="size-9 shrink-0 rounded-md flex items-center justify-center bg-concern-accent text-concern-accent-dot text-lg">
                            <i class="ph ph-storefront"></i>
                        </span>
                        <div class="flex flex-col min-w-0">
                            <h2 class="font-display font-semibold text-text truncate">{{ venue.name }}</h2>
                            @if (venue.settings?.hostname; as host) {
                                <span class="text-xs text-text-muted truncate">{{ host }}</span>
                            }
                        </div>
                    </div>
                    <div class="flex flex-wrap gap-2 mt-auto">
                        <a pButton size="small" [href]="auth.clientAppUrl(venue._id, 'calendar')" target="_blank" rel="noopener">
                            <i class="ph ph-calendar-blank"></i>
                            <span>{{ 'OPEN_CALENDAR' | translate }}</span>
                        </a>
                        <a pButton size="small" [outlined]="true" severity="secondary" [href]="auth.clientAppUrl(venue._id)" target="_blank" rel="noopener">
                            <i class="ph ph-arrow-square-out"></i>
                            <span>{{ 'OPEN_VENUE_ADMIN' | translate }}</span>
                        </a>
                    </div>
                </article>
            } @empty {
                <fb-empty-state
                    class="md:col-span-2 2xl:col-span-3"
                    icon="ph-storefront"
                    [title]="(search() ? 'NO_VENUES_MATCH' : 'NO_VENUE_ACCESS_YET') | translate"
                    [text]="(search() ? '' : 'NO_VENUE_ACCESS_YET_TEXT') | translate" />
            }
        </div>
    `
})
export class VenuesComponent {
    auth = inject(AuthService);
    search = signal('');

    venues = computed(() => {
        const term = this.search().trim().toLowerCase();
        return this.auth
            .accessibleVenues()
            .filter((venue) => !term || venue.name?.toLowerCase().includes(term))
            .sort((a, b) => String(a.name).localeCompare(String(b.name)));
    });
}
